import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SearchBar from "../common/SearchBar/SearchBar";
import type { SearchResult } from "../Dashboard/TopBar/useGlobalSearch";
import {
  manualHoldingsFormSchema,
  type ManualHoldingsFormData,
} from "./schemas/manualHoldingsSchema";
import { useAddManualHoldings } from "./hooks/useAddManualHoldings";
import ManualHoldingRow from "./ManualHoldingRow";

interface ManualHolding {
  ticker: string;
  shares: number;
  averagePrice: number;
}

interface ManualHoldingsFormProps {
  initialHoldings?: ManualHolding[];
  isLoadingInitialData?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const ManualHoldingsForm: React.FC<ManualHoldingsFormProps> = ({
  initialHoldings = [],
  isLoadingInitialData = false,
  onSuccess,
  onCancel,
}) => {
  const theme = useTheme();
  const [searchBarKey, setSearchBarKey] = useState(0);
  const addManualHoldingsMutation = useAddManualHoldings();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
  } = useForm<ManualHoldingsFormData>({
    resolver: zodResolver(manualHoldingsFormSchema),
    mode: "onChange",
    defaultValues: {
      holdings: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "holdings",
  });

  const holdings = watch("holdings");

  // Populate form with initial holdings when they're loaded
  useEffect(() => {
    if (initialHoldings && initialHoldings.length > 0) {
      reset({
        holdings: initialHoldings,
      });
    }
  }, [initialHoldings, reset]);

  // Handle stock selection from SearchBar
  const handleAddStock = (searchResult: SearchResult) => {
    // Check if ticker already exists
    const tickerExists = holdings.some(
      (h) => h.ticker?.toUpperCase() === searchResult.symbol.toUpperCase()
    );

    if (tickerExists) {
      // Clear the SearchBar by forcing a remount
      setSearchBarKey((prev) => prev + 1);
      return;
    }

    // Add new holding with ticker pre-filled
    append({
      ticker: searchResult.symbol.toUpperCase(),
      shares: 0,
      averagePrice: 0,
    });

    // Clear the SearchBar by forcing a remount
    setSearchBarKey((prev) => prev + 1);
  };

  const onSubmit = async (data: ManualHoldingsFormData) => {
    try {
      await addManualHoldingsMutation.mutateAsync({ holdings: data.holdings });
      onSuccess();
    } catch (error) {
      // Error is handled by the mutation hook
    }
  };

  const canSubmit =
    isValid &&
    (holdings.length === 0 || holdings.every((h) => h.ticker && h.shares > 0 && h.averagePrice > 0)) &&
    !addManualHoldingsMutation.isPending &&
    !isLoadingInitialData;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Loading skeleton while fetching initial data */}
      {isLoadingInitialData && (
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={80} />
        </Box>
      )}

      {/* Add Stock Section - Fixed at top */}
      {!isLoadingInitialData && (
        <Box sx={{ mb: 3, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AddIcon sx={{ color: "#22c55e", fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Add Stocks
            </Typography>
          </Box>

          <SearchBar
            key={searchBarKey}
            placeholder="Search and add stocks by name or ticker..."
            width="100%"
            variant="page"
            onSelectItem={handleAddStock}
          />

          {fields.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Search for a company to get started
            </Typography>
          )}
        </Box>
      )}

      {!isLoadingInitialData && fields.length > 0 && <Divider sx={{ my: 2, flexShrink: 0 }} />}

      {/* Holdings List - Scrollable */}
      {!isLoadingInitialData && fields.length > 0 && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, flexShrink: 0 }}>
            Your Holdings ({fields.length})
          </Typography>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.02)",
              borderRadius: 3,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {fields.map((field, index) => (
                <ManualHoldingRow
                  key={field.id}
                  field={field}
                  index={index}
                  control={control}
                  ticker={holdings[index]?.ticker}
                  shares={holdings[index]?.shares || 0}
                  averagePrice={holdings[index]?.averagePrice || 0}
                  onRemove={remove}
                />
              ))}
            </Box>
          </Box>

          {/* Form-level error */}
          {errors.holdings?.message && typeof errors.holdings.message === "string" && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
              {errors.holdings.message}
            </Typography>
          )}
        </Box>
      )}

      {/* Action Buttons - Fixed at bottom */}
      {!isLoadingInitialData && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            pt: 2,
            borderTop:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            flexShrink: 0,
          }}
        >
        <Button
          onClick={onCancel}
          disabled={addManualHoldingsMutation.isPending}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            borderRadius: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!canSubmit}
          startIcon={
            addManualHoldingsMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            backgroundColor: "#22c55e",
            color: "white",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "#16a34a",
            },
            "&:disabled": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          {addManualHoldingsMutation.isPending ? "Saving..." : "Save Holdings"}
        </Button>
      </Box>
      )}
    </Box>
  );
};

export default ManualHoldingsForm;